import { useEffect, useRef, useState } from 'react';
import type { ChartAccountRow } from '@/core/models/trial-balance.models';
import { notifyError } from '@/core/notify/notify';
import { trialBalanceService } from '@/core/services/trialBalance';

/** As the Chart of Accounts search box: the search waits until typing pauses (TaxreturnChartofaccounts, 400 ms). */
export const ACCOUNT_SUGGESTION_DELAY_MS = 400;
/** Fewer characters than this ask for nothing - a single letter matches most of any chart. */
export const ACCOUNT_SUGGESTION_MIN_LENGTH = 2;

/** Active accounts only - the same accounts the Account dropdown offers. */
const ACTIVE = 1;

export interface AccountSuggestionState {
  /** The grid row the suggestions are for; null when none are shown. */
  row: number | null;
  /** The field the list hangs from. */
  anchor: HTMLElement | null;
  accounts: ChartAccountRow[];
  /** A search for `row` has answered (so an empty `accounts` means nothing matched). */
  searched: boolean;
  /** The highlighted suggestion (arrow keys). */
  active: number;
}

const NONE: AccountSuggestionState = { row: null, anchor: null, accounts: [], searched: false, active: 0 };

/**
 * #190: accounts from the client's chart that match the words typed into a line's Particular, using the Chart of
 * Accounts search (ChartOfAccounts/getAccounts): case-insensitive, part-words, on the account name, code, type and
 * group - what "matching" means on the Chart of Accounts screen. Nothing is chosen for the user; the list only offers.
 * A newer search always wins over the answer to an older one.
 */
export function useAccountSuggestions(clientId: string) {
  const [state, setState] = useState<AccountSuggestionState>(NONE);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const token = useRef(0);

  const stop = () => {
    if (timer.current) {
      clearTimeout(timer.current);
      timer.current = null;
    }
    token.current++;
  };

  useEffect(() => stop, []);

  /** Clears the suggestions (a choice was made, the list was dismissed, or the lines changed shape). */
  const clear = () => {
    stop();
    setState(NONE);
  };

  /** Searches for `text` typed into row `row`'s Particular once typing pauses. */
  const search = (row: number, text: string, anchor: HTMLElement | null) => {
    stop();
    const term = text.trim();
    if (!clientId || term.length < ACCOUNT_SUGGESTION_MIN_LENGTH) {
      setState(NONE);
      return;
    }
    const current = token.current;
    // Another row's list closes at once; this row's stays up until the new answer replaces it.
    setState((previous) => (previous.row === row ? { ...previous, anchor } : { ...NONE, row, anchor }));
    timer.current = setTimeout(() => {
      timer.current = null;
      // Started inside the chain so a failure of any kind is reported, never thrown from the timer.
      Promise.resolve()
        .then(() => trialBalanceService.getAccounts(clientId, { search: term, status: ACTIVE, accountTypeId: null }))
        .then(
          (accounts) => {
            if (current !== token.current) return;
            setState({ row, anchor, accounts, searched: true, active: 0 });
          },
          (error) => {
            if (current !== token.current) return;
            setState(NONE);
            notifyError(error);
          },
        );
    }, ACCOUNT_SUGGESTION_DELAY_MS);
  };

  const setActive = (active: number) => setState((previous) => ({ ...previous, active }));

  return { state, search, clear, setActive };
}
